import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from './Button';
import type { HeroContent, ImageSlideContent, QualitySlideContent, MapSlideContent } from '../lib/types';
import { fetchHeroContent, fetchHeroSlides } from '../lib/sanity';
import {
  defaultHeroContent,
  defaultImageSlide,
  defaultQualitySlide,
  defaultMapSlide,
} from '../lib/defaultContent';

// ─── Slide transition variants ────────────────────────────────────────────────

const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
};

// ─── Hero ─────────────────────────────────────────────────────────────────────

interface HeroProps {
  content?: HeroContent;
}

const SLIDE_INTERVAL = 9500;

const Hero: React.FC<HeroProps> = ({ content }) => {
  const [heroContent, setHeroContent] = useState<HeroContent>(
    content || defaultHeroContent
  );
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [imageSlide, setImageSlide] = useState<ImageSlideContent>(defaultImageSlide);
  const [qualitySlide, setQualitySlide] = useState<QualitySlideContent>(defaultQualitySlide);
  const [mapSlide, setMapSlide] = useState<MapSlideContent>(defaultMapSlide);
  const [carouselEnabled, setCarouselEnabled] = useState(true);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useTransform(mouseY, [-300, 300], [4, -4]);
  const rotateY = useTransform(mouseX, [-300, 300], [-4, 4]);

  useEffect(() => {
    if (content) return;
    fetchHeroContent().then((data) => {
      if (data) setHeroContent(data);
    });
  }, [content]);

  // Fetch Sanity slide overrides and carousel setting
  useEffect(() => {
    fetchHeroSlides().then(({ imageSlide: is, qualitySlide: qs, mapSlide: ms, useCarousel }) => {
      if (is) setImageSlide(is);
      if (qs) setQualitySlide(qs);
      if (ms) setMapSlide(ms);
      setCarouselEnabled(useCarousel);
    });
  }, []);

  const [userPaused, setUserPaused] = useState(false);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (userPaused || !carouselEnabled) return;
    const timer = setInterval(() => {
      setActiveSlide(prev => {
        setDirection(1);
        return (prev + 1) % 3;
      });
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [userPaused, carouselEnabled]);

  const goToSlide = (index: number) => {
    setDirection(index > activeSlide ? 1 : -1);
    setActiveSlide(index);
    // Pause auto-switching for 60 seconds on manual interaction
    setUserPaused(true);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = setTimeout(() => setUserPaused(false), 60000);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    mouseX.set(event.clientX - rect.left - rect.width / 2);
    mouseY.set(event.clientY - rect.top - rect.height / 2);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  // Build slides array and sort by order property
  const buildSlides = () => {
    if (!carouselEnabled) {
      return [<ImageSlideComp key="image" data={imageSlide} />];
    }

    const slideData = [
      { order: imageSlide.order ?? 1, component: <ImageSlideComp key="image" data={imageSlide} /> },
      { order: mapSlide.order ?? 2, component: <EastAfricaMap key="map" data={mapSlide} /> },
      { order: qualitySlide.order ?? 3, component: <QualitySlideComp key="quality" data={qualitySlide} /> },
    ];

    return slideData.sort((a, b) => a.order - b.order).map(slide => slide.component);
  };

  const slides = buildSlides();

  return (
    <section className="relative grain-background pt-14 pb-8 md:pt-28 md:pb-12 flex items-center">
      <div className="container-custom relative" style={{ zIndex: 30 }}>
        <div className="grid lg:grid-cols-[52%_48%] gap-8 md:gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-5 sm:space-y-8 text-center md:text-left"
          >
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-playfair font-bold text-primary-black leading-tight mb-4">
                {heroContent.title}{' '}
                <span className="text-primary-red">{heroContent.titleHighlight}</span>
              </h1>
              <div className="relative max-w-2xl mb-4 sm:mb-5 pb-1 mx-auto md:mx-0">
                <p className="text-sm sm:text-lg md:text-2xl text-gray-700 leading-relaxed">
                  {heroContent.subtitle}
                </p>
                {/* Animated underline — draws in left→right, holds, erases right→left once */}
                <motion.span
                  className="absolute bottom-0 left-0 h-0.5 bg-secondary-sage hidden sm:block"
                  style={{ width: '100%', transformOrigin: 'left center' }}
                  animate={{ scaleX: [0, 1, 1, 0] }}
                  transition={{
                    duration: 2.8,
                    times: [0, 0.38, 0.65, 1],
                    ease: 'easeInOut',
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mb-2 justify-center md:justify-start">
                {[
                  'Structured Procurement',
                  'Quality Control',
                  'Supplier Relationships',
                  'Delivery Coordination',
                ].map((strength) => (
                  <div
                    key={strength}
                    className="flex items-center gap-2 text-sm font-inter font-medium text-gray-700"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-red flex-shrink-0" />
                    {strength}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start">
              {heroContent.ctas.map((cta, index) => (
                <Button key={index} variant={cta.variant} href={cta.href}>
                  {cta.text}
                </Button>
              ))}
            </div>
          </motion.div>

          {/* 3D card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative hidden lg:block"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <div className="perspective-1000">
              <motion.div
                style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
                transition={{ type: 'spring', stiffness: 60, damping: 20 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-secondary-terracotta/20 to-secondary-sage/20 rounded-2xl blur-3xl" />

                <div className="relative w-full h-[540px] rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
                  <AnimatePresence custom={direction} initial={false}>
                    <motion.div
                      key={activeSlide}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                      className="absolute inset-0"
                    >
                      {slides[activeSlide]}
                    </motion.div>
                  </AnimatePresence>

                  {/* Dot indicators - only show if carousel enabled */}
                  {carouselEnabled && (
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                      {[0, 1, 2].map(i => (
                        <button
                          key={i}
                          onClick={() => goToSlide(i)}
                          className={`h-2 rounded-full transition-all duration-300 ${
                            i === activeSlide
                              ? 'w-6 bg-primary-red'
                              : 'w-2 bg-gray-400 hover:bg-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ─── Slide 1 (position 0): Image slide ────────────────────────────────────────

const ImageSlideComp: React.FC<{ data: ImageSlideContent }> = ({ data }) => {
  const hasPhrases = data.phrases && data.phrases.length > 0;
  const phrases = hasPhrases ? data.phrases! : (defaultImageSlide.phrases || []);
  const [idx, setIdx] = useState(0);

  // Advance through phrases once — stops at the last one, does not loop
  useEffect(() => {
    if (!hasPhrases || phrases.length === 0) return;
    const intervalId = setInterval(() => {
      setIdx(p => {
        if (p >= phrases.length - 1) {
          clearInterval(intervalId);
          return p;
        }
        return p + 1;
      });
    }, 2200);
    return () => clearInterval(intervalId);
  }, [phrases.length, hasPhrases]);

  const phrase = phrases[idx] || { line1: '', line2: '' };
  const imageUrl = data.imageUrl || defaultImageSlide.imageUrl || '/custom_content/nicetruck.jpeg';

  const content = (
    <div className="relative w-full h-full overflow-hidden bg-gray-900">
      <img
        src={imageUrl}
        alt="Marketing visual"
        className="absolute inset-0 w-full h-full object-cover opacity-75"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />

      {hasPhrases && phrases.length > 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.55, ease: 'easeInOut' }}
              className="text-center"
            >
              {data.brandLabel && (
                <p className="text-white/70 text-xs uppercase tracking-widest font-inter mb-3">
                  {data.brandLabel}
                </p>
              )}
              <h2 className="text-white font-playfair font-bold text-4xl leading-tight drop-shadow-lg">
                {phrase.line1}
              </h2>
              <h2 className="text-primary-red font-playfair font-bold text-4xl leading-tight drop-shadow-lg">
                {phrase.line2}
              </h2>
            </motion.div>
          </AnimatePresence>
        </div>
      )}
    </div>
  );

  // Wrap with link if provided
  if (data.link) {
    return (
      <Link to={data.link} className="block w-full h-full cursor-pointer hover:opacity-95 transition-opacity">
        {content}
      </Link>
    );
  }

  return content;
};

// ─── Slide 2 (position 1): East Africa Map ────────────────────────────────────
// Dark background, country-name labels, trade route animation (UG, KE, TZ)

const MAP_COUNTRIES = [
  { id: 'ug', label: 'Uganda',   x: 693, y: 492 },
  { id: 'ke', label: 'Kenya',    x: 772, y: 487 },
  { id: 'tz', label: 'Tanzania', x: 734, y: 612 },
];

const MAP_ROUTES = [
  { from: 'ug', to: 'ke' },
  { from: 'ke', to: 'tz' },
  { from: 'ug', to: 'tz' },
];


const EastAfricaMap: React.FC<{ data: MapSlideContent }> = ({ data }) => {
  const countryIndex = Object.fromEntries(MAP_COUNTRIES.map(c => [c.id, c]));

  // Use Sanity data if available, otherwise use defaults from defaultMapSlide
  const title = data?.title || defaultMapSlide.title;
  const subtitle = data?.subtitle || defaultMapSlide.subtitle;
  const description = data?.description || defaultMapSlide.description;

  return (
    <div className="relative w-full h-full overflow-hidden" style={{ background: '#0f172a' }}>

      {/* Background radial glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          inset: 0,
          background:
            'radial-gradient(ellipse 55% 55% at 58% 52%, rgba(156,175,136,0.09) 0%, transparent 70%)',
        }}
      />

      {/* Top header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="absolute top-0 left-0 right-0 z-10 px-6 pt-5"
      >
        <div className="flex items-center gap-2 mb-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-emerald-400/80 text-[10px] font-inter uppercase tracking-widest">
            Live Network
          </span>
        </div>
        <h3 className="text-white font-playfair font-bold text-xl leading-tight">
          {title}
        </h3>
        <p className="text-white/45 font-inter text-xs mt-0.5 tracking-wide">
          {subtitle}
        </p>
        {description && (
          <p className="text-white/35 font-inter text-[10px] mt-1 leading-relaxed">
            {description}
          </p>
        )}
      </motion.div>

      {/* SVG map — shifted down slightly to leave room for header */}
      <svg
        viewBox="620 410 260 340"
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="East Africa map"
        style={{ marginTop: '16px' }}
      >
        {/* Country fills */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          fill="#4a6741"
          fillOpacity="0.9"
          stroke="#9CAF88"
          strokeWidth="0.6"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        >
          <motion.path id="KE"
            d="m 807.2,463.1 -8.4,0 -4.9,-4.7 -11.1,5.8 -3.5,5.8 -8.2,-1.1 -2.7,-1.6 -2.9,0.4 -3.8,-0.2 -15.7,-11.7 -8.5,0 -4.2,-4.6 -0.1,-7.7 -6.4,-2.4 -8.1,9.1 -7.4,8.3 5.9,9.6 1.5,7 5.5,15.8 -4.4,10.1 -5.9,9.2 -3.5,5.6 0,0.7 2.9,5.2 -0.8,10.3 44.1,28.2 0.7,8 17.3,13.8 5,-4.6 2.5,-9.2 4,-5.5 1.9,-9.8 4.6,-1 3.1,-5.8 8.6,-5.5 -7.2,-11.4 -0.4,-50.4 10.5,-15.7 z"
            whileHover={{ fill: '#5d8054', filter: 'drop-shadow(0 8px 12px rgba(0,0,0,0.5))' }}
            style={{ cursor: 'pointer' }}
            transition={{ duration: 0.2 }}
          />
          <motion.path id="RW"
            d="m 667.9,533 -7.4,4.3 -2.9,-1.4 -3.5,3.8 -0.5,8.3 -1.7,1 -1.2,7.6 7.4,1.1 3.8,-7.9 6.5,0.9 0,0 3.5,-1.8 0.8,-8.1 -4.8,-7.8 z"
            whileHover={{ fill: '#5d8054' }}
            style={{ cursor: 'pointer' }}
            transition={{ duration: 0.2 }}
          />
          <motion.path id="BI"
            d="m 669,556.1 -0.6,-5.4 0,0 -6.5,-0.9 -3.8,7.9 -7.4,-1.1 3,6.3 0.1,2.4 4.3,13.2 0,0.6 1.2,-0.2 4.5,-5 4.9,-7.2 3,-2.9 -0.1,-4.5 -2.6,-3.2 z"
            whileHover={{ fill: '#5d8054' }}
            style={{ cursor: 'pointer' }}
            transition={{ duration: 0.2 }}
          />
          <motion.path id="TZ"
            d="m 672.2,531.3 -4.3,1.7 4.8,7.8 -0.8,8.1 -3.5,1.8 0,0 0.6,5.4 2.6,3.2 0.1,4.5 -3,2.9 -4.9,7.2 -4.5,5 -1.2,0.2 -0.7,5.9 2.3,2 -0.5,5.9 2.3,5.5 -2.9,5.3 9.7,9.4 0.8,8.5 5.9,14.2 0,0 0.6,0.4 4.8,2.3 7.7,2.4 6.8,4.1 11.9,2.6 2.3,3.8 0,0 0.8,-2.7 6.2,7.4 0.6,14.5 3.9,5.3 -0.1,0.2 4.7,-0.5 14.4,3.9 3.3,-1.8 8.4,-0.3 4.5,-4.2 7.3,0.2 13.4,-5.3 10,-8.1 0,0 -4.4,-3 -4.7,-13.6 -4,-8.7 1,-6.6 -0.6,-4.2 3.5,-8.4 -0.3,-3.6 -7.7,-5 -0.6,-7.8 5.9,-17.1 -17.3,-13.8 -0.7,-8 -44.1,-28.2 0,0 -6,6.1 -4.1,6.3 4.8,4.7 -7,3.4 -1.5,-1.6 -7.1,0.9 -5.5,3.1 -3.3,-5.4 2.3,-9.7 0.5,-8.3 0,0 0,0 -13.4,-0.2 z"
            whileHover={{ fill: '#5d8054', filter: 'drop-shadow(0 8px 12px rgba(0,0,0,0.5))' }}
            style={{ cursor: 'pointer' }}
            transition={{ duration: 0.2 }}
          />
          <motion.path id="UG"
            d="m 711.3,458.5 -7.4,6.4 -8.6,-0.1 -9.8,3.3 -7.8,-3.1 -5,3.8 0,0 -0.6,16.1 4.9,1.9 -3.9,4.9 -4.7,3.7 -4.6,7.2 -2.6,6.4 -0.6,11.1 -2.9,5.3 -0.1,10.5 2.9,1.4 7.4,-4.3 4.3,-1.7 13.4,0.2 0,0 -0.7,-5.3 5.7,-8.1 7.7,-2 5.2,-3.3 6.3,2.7 0.6,1 0,-0.7 3.5,-5.6 5.9,-9.2 4.4,-10.1 -5.5,-15.8 -1.5,-7 -5.9,-9.6 z"
            whileHover={{ fill: '#5d8054', filter: 'drop-shadow(0 8px 12px rgba(0,0,0,0.5))' }}
            style={{ cursor: 'pointer' }}
            transition={{ duration: 0.2 }}
          />
        </motion.g>

        {/* Trade route lines — UG, KE, TZ only */}
        {MAP_ROUTES.map((route, i) => {
          const from = countryIndex[route.from];
          const to = countryIndex[route.to];
          return (
            <g key={`route-${i}`}>
              <line
                x1={from.x} y1={from.y}
                x2={to.x}   y2={to.y}
                stroke="rgba(220,38,38,0.3)"
                strokeWidth="1"
                strokeDasharray="5 4"
              />
              <motion.circle
                r="2.5"
                fill="#DC2626"
                initial={{ x: from.x, y: from.y, opacity: 0 }}
                animate={{ x: [from.x, to.x], y: [from.y, to.y], opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2.6, delay: i * 0.8, repeat: Infinity, ease: 'easeInOut' }}
              />
            </g>
          );
        })}

        {/* Country name labels + nodes */}
        {MAP_COUNTRIES.map((country, i) => (
          <motion.g
            key={country.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 + i * 0.15 }}
          >
            <motion.circle
              cx={country.x} cy={country.y}
              r="5"
              fill="none"
              stroke="rgba(220,38,38,0.6)"
              strokeWidth="1"
              animate={{ r: [5, 10], opacity: [0.7, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.55 }}
            />
            <circle cx={country.x} cy={country.y} r="3" fill="#DC2626" />
            <circle cx={country.x} cy={country.y} r="1.5" fill="white" />
            <text
              x={country.x + 6}
              y={country.y + 3.5}
              fill="rgba(255,255,255,0.85)"
              fontSize="8.5"
              fontFamily="Inter, sans-serif"
              fontWeight="600"
              letterSpacing="0.3"
            >
              {country.label}
            </text>
          </motion.g>
        ))}
      </svg>

    </div>
  );
};

// ─── Slide 3 (position 2): Quality Assurance ──────────────────────────────────

const QualitySlideComp: React.FC<{ data: QualitySlideContent }> = ({ data }) => {
  const points = data.points.length > 0 ? data.points : defaultQualitySlide.points;
  const metrics = data.metrics.length > 0 ? data.metrics : defaultQualitySlide.metrics;
  const [active, setActive] = useState(0);
  const videoUrl = data.videoUrl || defaultQualitySlide.videoUrl;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const t = setInterval(() => setActive(p => (p + 1) % points.length), 2400);
    return () => clearInterval(t);
  }, [points.length]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Video autoplay prevented:', err);
      });
    }
  }, [videoUrl]);

  return (
    <div className="relative w-full h-full overflow-hidden bg-gray-950">
      {videoUrl ? (
        <video
          ref={videoRef}
          key={videoUrl}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onLoadedData={(e) => {
            const video = e.currentTarget;
            video.play().catch(err => console.log('Video play error:', err));
          }}
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
      ) : (
        <img
          src="/custom_content/warehousegreensks2.jpeg"
          alt="Warehouse sacks"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-gray-900/80" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_40%,rgba(156,175,136,0.12),transparent_60%)]" />

      <div className="absolute inset-0 flex flex-col justify-between p-8">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <p className="text-[#9CAF88] text-xs uppercase tracking-widest font-inter mb-1">
            Quality Assurance
          </p>
          <h3 className="text-white font-playfair text-3xl font-bold leading-tight">
            {data.title || defaultQualitySlide.title}
            <br />
            <span className="text-[#9CAF88]">{data.tagline || defaultQualitySlide.tagline}</span>
          </h3>
        </motion.div>

        <div className="flex-1 flex flex-col justify-center gap-4 my-6">
          {points.map((point, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: i === active ? 1 : 0.35, x: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="flex items-center gap-3"
            >
              <motion.div
                className="w-2 h-2 rounded-full flex-shrink-0"
                animate={{ backgroundColor: i === active ? '#DC2626' : '#9CAF88' }}
                transition={{ duration: 0.3 }}
              />
              <span
                className={`font-inter text-sm leading-snug transition-colors duration-300 ${
                  i === active ? 'text-white font-medium' : 'text-white/50'
                }`}
              >
                {point}
              </span>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="grid gap-3"
          style={{ gridTemplateColumns: `repeat(${metrics.length}, 1fr)` }}
        >
          {metrics.map((m, i) => (
            <div
              key={i}
              className="rounded-xl border border-white/10 px-3 py-3 text-center backdrop-blur-sm"
              style={{ background: 'rgba(255,255,255,0.05)' }}
            >
              <div className="text-[#9CAF88] font-playfair font-bold text-xl">{m.value}</div>
              <div className="text-white/50 font-inter text-[10px] uppercase tracking-wide mt-0.5">
                {m.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Hero;
